import {UserRoundCog} from 'lucide-react'
import React, {FC, useState} from 'react'
import {useIntl} from 'react-intl'

import type {AllocationServerGrid} from '../../types/allocationServer'

/**
 * A class subject a file names for one teacher while another teacher already has it
 * (`heldByOther`, AI server): the server keeps the current teacher and the page asks.
 */
export type HeldByOtherCell = {
  subjectId: number
  /** The teacher the file names for the cell. */
  teacherId: number
  /** The teacher who has the cell now. */
  holderId: number
  teacher?: string | null
  holder?: string | null
  class?: string | null
  subject?: string | null
  /** The server's sentence about the cell, shown as it is instead of our own wording. */
  message?: string | null
}

/** One question per cell and file teacher: the key its answer is kept under (`subjectId|teacherId`). */
export const heldCellKey = (cell: Pick<HeldByOtherCell, 'subjectId' | 'teacherId'>): string =>
  `${cell.subjectId}|${cell.teacherId}`

/** A held cell the server already named: its sentence and holder need no grid to look them up. */
export const heldCellNamed = (cell: HeldByOtherCell): boolean =>
  Boolean(cell.message?.trim() && cell.holder?.trim())

/**
 * The prefill's `heldByOther` with the names the page shows: what the server sent, else teachers
 * from the grid's teacher list and the class and subject from the cell (a whole-school grid, no
 * scope).
 */
export const heldCellsWithNames = (
  held: ReadonlyArray<HeldByOtherCell>,
  grid: AllocationServerGrid | null
): HeldByOtherCell[] => {
  const teachers = new Map((grid?.teachers ?? []).map((teacher) => [teacher.id, teacher.name]))
  const columns = new Map((grid?.columns ?? []).map((column) => [column.key, column.name]))
  const places = new Map<number, {class: string; subject: string}>()
  grid?.branches.forEach((branch) =>
    branch.rows.forEach((row) =>
      Object.entries(row.cells).forEach(([key, cell]) =>
        places.set(cell.subjectId, {
          class: row.displayName || row.name,
          subject: columns.get(key) ?? key,
        })
      )
    )
  )
  return held.map((cell) => ({
    ...cell,
    teacher: cell.teacher || teachers.get(cell.teacherId) || null,
    holder: cell.holder || teachers.get(cell.holderId) || null,
    class: cell.class || places.get(cell.subjectId)?.class || null,
    subject: cell.subject || places.get(cell.subjectId)?.subject || null,
  }))
}

type Props = {
  cells: HeldByOtherCell[]
  /**
   * The questions already answered (`heldCellKey`). Kept by the page, not here: this list leaves
   * the screen with the landing (the grid, Versi), and its answers must not come back with it.
   */
  answered?: ReadonlySet<string>
  /** Keep, or a Replace that went through: the question is answered. Without it, kept here. */
  onAnswered?: (cell: HeldByOtherCell) => void
  /** "Replace": the cell gets the file's teacher (PATCH the cell's teacher). Rejects on failure. */
  onReplace: (cell: HeldByOtherCell) => Promise<unknown>
}

/**
 * One question per cell another teacher already has: "Ali Khan teaches Maths in Grade 7-A; the
 * file says Sara Ahmed. Replace?" Nothing is replaced without a yes; Keep leaves the cell as it
 * is. An answered cell leaves the list.
 */
const HeldByOtherQuestions: FC<Props> = ({cells, answered, onAnswered, onReplace}) => {
  const intl = useIntl()
  // Answers kept here only when the page does not keep them (`answered` / `onAnswered`).
  const [ownAnswered, setOwnAnswered] = useState<ReadonlySet<string>>(() => new Set())
  const [busy, setBusy] = useState<number | null>(null)
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.HELD.${id}`}, values)

  const answeredNow = answered ?? ownAnswered
  const open = cells.filter((cell) => !answeredNow.has(heldCellKey(cell)))
  if (open.length === 0) return null
  const answer = (cell: HeldByOtherCell) =>
    onAnswered ? onAnswered(cell) : setOwnAnswered((prev) => new Set(prev).add(heldCellKey(cell)))

  return (
    <section className='ta-card ta-held' aria-label={t('TITLE', {count: open.length})}>
      <p className='ta-held__title'>
        <UserRoundCog size={18} aria-hidden />
        {t('TITLE', {count: open.length})}
      </p>
      <ul className='ta-held__list'>
        {open.map((cell) => {
          const values = {
            holder: cell.holder?.trim() || t('SOMEONE'),
            teacher: cell.teacher?.trim() || t('SOMEONE'),
            subject: cell.subject?.trim() || '',
            class: cell.class?.trim() || '',
          }
          return (
            <li key={heldCellKey(cell)} className='ta-held__item'>
              {cell.message?.trim() ? (
                // The server's sentence ("… is already assigned to Tahir Nawaz in this file"),
                // then the choice in our words.
                <p>
                  {cell.message.trim()} {t('REPLACE_ASK', {teacher: values.teacher})}
                </p>
              ) : (
                <p>
                  {t(values.subject && values.class ? 'QUESTION' : 'QUESTION_NO_PLACE', values)}
                </p>
              )}
              <div className='ta-held__actions'>
                <button
                  type='button'
                  className='ta-btn ta-btn--primary ta-btn--sm'
                  disabled={busy !== null}
                  onClick={() => {
                    setBusy(cell.subjectId)
                    onReplace(cell)
                      .then(() => answer(cell))
                      // Refused: the question stays (the failure was said by the caller).
                      .catch(() => undefined)
                      .finally(() => setBusy(null))
                  }}
                >
                  {busy === cell.subjectId && (
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden />
                  )}
                  {t('REPLACE')}
                </button>
                <button
                  type='button'
                  className='ta-btn ta-btn--outline ta-btn--sm'
                  disabled={busy === cell.subjectId}
                  onClick={() => answer(cell)}
                >
                  {t('KEEP', {holder: values.holder})}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default HeldByOtherQuestions
